import React from 'react';
import { cn } from '@/utils/cn';
import Image from '@/components/common/Image';

interface FeatureItem {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
}

interface InfrastructureProps extends React.HTMLAttributes<HTMLDivElement> {
  tag: string;
  title: string;
  description: string;
  features: FeatureItem[];
}

const Infrastructure = ({ className, tag, title, description, features, ...props }: InfrastructureProps) => {
  // First two features for the special first row
  const firstRowFeatures = features.slice(0, 2);
  // Remaining features for the regular grid
  const remainingFeatures = features.slice(2);

  return (
    <section className={cn("py-40 px-25 bg-black text-white", className)} {...props}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="inline-block text-sm font-semibold text-blue-400 tracking-wider mb-4">
            {tag}
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            {title}
          </h2>
          <p className="text-lg text-gray-400">
            {description}
          </p>
        </div>

        {/* Special First Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* First feature (2x width) */}
          <div className="md:col-span-2 bg-[#161616] rounded-lg p-8 flex flex-col border border-[#242424]">
            <div className="relative mb-6 rounded-lg overflow-hidden p-4">
              <Image
                src={firstRowFeatures[0].imageSrc}
                alt={firstRowFeatures[0].imageAlt}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
            <h3 className="text-xl font-semibold text-white mb-4">
              {firstRowFeatures[0].title}
            </h3>
            <p className="text-gray-400">
              {firstRowFeatures[0].description}
            </p>
          </div>

          {/* Second feature (normal width) */}
          <div className="bg-[#161616] rounded-lg p-8 flex flex-col border border-[#242424]">
            <div className="relative mb-6 rounded-lg overflow-hidden p-4">
              <Image
                src={firstRowFeatures[1].imageSrc}
                alt={firstRowFeatures[1].imageAlt}
                width={560}
                height={320}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>
            <h3 className="text-xl font-semibold text-white mb-4">
              {firstRowFeatures[1].title}
            </h3>
            <p className="text-gray-400">
              {firstRowFeatures[1].description}
            </p>
          </div>
        </div>

        {/* Regular Grid for Remaining Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {remainingFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-[#161616] rounded-lg p-8 flex flex-col border border-[#242424]"
            >
              <div className="relative mb-6 rounded-lg overflow-hidden p-4">
                <Image
                  src={feature.imageSrc}
                  alt={feature.imageAlt}
                  width={560}
                  height={320}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-400">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Infrastructure;